import { Loader2, RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  AUDIENCE_WIZARD_BUTTON_CLASS,
  AUDIENCE_WIZARD_BUTTON_VARIANT,
} from '@/lib/constants/audience-wizard'
import { InferredMixEditor } from '../../../components/InferredMixEditor'
import type {
  IdentityFieldSummary,
} from '@/types/components/audiences/import-session'
import type { InferenceDistributionEntry } from '@/types/components/audiences/inferred-mix-editor'

interface IdentityInferencePanelProps {
  field: IdentityFieldSummary
  draftDistribution: InferenceDistributionEntry[] | null
  isPatching: boolean
  onDraftDistributionChange: (
    value: InferenceDistributionEntry[] | null,
  ) => void
  onSubmit: () => void
  /** Reset draft to suggested inference (Figma "Undo changes"). */
  onUndo?: () => void
}

function isInferenceDirty(
  field: IdentityFieldSummary,
  draftDistribution: InferenceDistributionEntry[] | null,
) {
  if (!draftDistribution) {
    return false
  }
  const suggestedMap = new Map(
    (field.suggestedInference?.values ?? []).map((value) => [
      value.value,
      value.percentage,
    ]),
  )
  return draftDistribution.some(
    (entry) =>
      (entry.percentage > 0 && suggestedMap.get(entry.value) !== entry.percentage) ||
      (entry.percentage === 0 && suggestedMap.has(entry.value)),
  )
}

export function IdentityInferencePanel({
  field,
  draftDistribution,
  isPatching,
  onDraftDistributionChange,
  onSubmit,
  onUndo,
}: IdentityInferencePanelProps) {
  const { t } = useTranslation('app')
  const total = draftDistribution?.reduce((sum, entry) => sum + entry.percentage, 0) ?? 0
  const dirty = isInferenceDirty(field, draftDistribution)
  const isBalanced = total === 100
  const diff = 100 - total
  const isAgeField = field.canonicalId === 'std_dem_age'

  return (
    <div className="space-y-6">
      {draftDistribution && draftDistribution.length > 0 ? (
        <>
          <div className="space-y-1">
            <div className="flex items-end justify-between">
              <p className="text-lg font-medium text-foreground">
                {isAgeField ? t('audienceCreate.ageBandMix') : (field.suggestedInference?.summary ?? t('audienceCreate.adjustDistribution'))}
              </p>
              {/* Total + diff — desktop only (inline with title) */}
              <div className="hidden md:flex items-end gap-3">
                {!isBalanced && (
                  <span className="inline-flex items-center rounded-[25px] px-3 py-[3px] text-xs font-medium backdrop-blur-[4.55px] bg-destructive/15 text-destructive dark:bg-destructive/25 dark:text-destructive-foreground">
                    {diff > 0 ? t('audienceCreate.percentNeeded', { count: diff }) : t('audienceCreate.percentExceeding', { count: Math.abs(diff) })}
                  </span>
                )}
                <p className={cn(
                  'text-lg font-medium tabular-nums',
                  isBalanced ? 'text-primary' : 'text-destructive',
                )}>
                  {t('audienceCreate.totalPercent', { total })}
                </p>
              </div>
            </div>
            <p className="text-base text-muted-foreground">
              {t('audienceCreate.distributionRecommend')}
            </p>
            {/* Total + diff — mobile only (after description) */}
            <div className="flex md:hidden items-center gap-3 pt-1">
              {!isBalanced && (
                <span className="inline-flex items-center rounded-[25px] px-3 py-[3px] text-xs font-medium backdrop-blur-[4.55px] bg-destructive/15 text-destructive dark:bg-destructive/25 dark:text-destructive-foreground">
                  {diff > 0 ? t('audienceCreate.percentNeeded', { count: diff }) : t('audienceCreate.percentExceeding', { count: Math.abs(diff) })}
                </span>
              )}
              <p className={cn(
                'text-lg font-medium tabular-nums',
                isBalanced ? 'text-primary' : 'text-destructive',
              )}>
                {t('audienceCreate.totalPercent', { total })}
              </p>
            </div>
          </div>
          <InferredMixEditor
            entries={draftDistribution}
            onChange={(next) => onDraftDistributionChange(next)}
            disabled={isPatching}
            showTotal={false}
          />
        </>
      ) : (
        <div className="rounded-2xl border border-dashed border-border/70 px-6 py-8 text-base text-muted-foreground">
          {t('audienceCreate.noInferredMix')}
        </div>
      )}

      <div className="flex items-center justify-center gap-4 pt-5">
        {onUndo && draftDistribution && draftDistribution.length > 0 && (
          <Button
            type="button"
            onClick={onUndo}
            disabled={isPatching || !dirty}
            className="h-10 px-4 text-base font-medium tracking-[-0.02em] border border-input-focus bg-transparent text-foreground dark:text-input-focus hover:bg-input-focus/10"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            {t('audienceCreate.undoChanges')}
          </Button>
        )}
        <Button
          type="button"
          variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
          onClick={onSubmit}
          disabled={isPatching || total !== 100}
          className={cn('h-10 px-4', AUDIENCE_WIZARD_BUTTON_CLASS)}
        >
          {isPatching ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('audienceCreate.saving')}
            </>
          ) : (
            t('audienceCreate.saveChanges')
          )}
        </Button>
      </div>
    </div>
  )
}
